import { Mission } from "@/services/clientDataService";
import { formatEuroTruncated } from "@/lib/formatUtils";

const WORKING_DAYS_PER_YEAR = 219.96; // 18,33 jours/mois × 12 mois (base moyenne capacité développeur)

export interface PaymentModalityData {
  totalDays: number;
  defaultDevelopers: number;
  minDevelopers: number;
  maxDevelopers: number;
  defaultMonths: number;
  defaultMonthlyCost: number;
  minimumMonthlyCost: number;
  maximumMonthlyCost: number;
}

export function formatAnnualCost(annualCost: number): string {
  return `${formatEuroTruncated(annualCost)} / an`;
}

export function calculateDeveloperRules(totalDays: number): { min: number; max: number; default: number } {
  if (totalDays <= 60) return { min: 1, max: 2, default: 1 };
  if (totalDays <= 150) return { min: 1, max: 2, default: 1 };
  if (totalDays <= 250) return { min: 1.2, max: 2.4, default: 1.2 };
  if (totalDays <= 600) return { min: 1.4, max: 2.6, default: 1.4 };
  return { min: 1.6, max: 4, default: 1.6 };
}

export function calculateProjectMonths(totalDays: number, developers: number): number {
  const effectiveWorkingDaysPerMonth = WORKING_DAYS_PER_YEAR / 12;
  const duration = totalDays / effectiveWorkingDaysPerMonth / developers;
  return Math.round(duration * 10) / 10;
}

export function calculateMinimumMonthlyCost(
  developers: number,
  totalDays: number,
  projectMonths: number
): number {
  const effectiveWorkingDaysPerMonth = WORKING_DAYS_PER_YEAR / 12;
  const developersNeededPerMonth = totalDays / (effectiveWorkingDaysPerMonth * projectMonths);
  const developerCostPerMonth = 7300;
  const marginMultiplier = 1.2;
  return Math.round(developerCostPerMonth * developersNeededPerMonth * marginMultiplier);
}

export function calculateMonthlyCostFromTotal(totalCost: number, projectMonths: number): number {
  return Math.round(totalCost / projectMonths);
}

export function calculateDevelopersFromMonthlyCost(
  targetMonthlyCost: number,
  totalCost: number,
  totalDays: number
): number {
  const approximateMonths = totalCost / targetMonthlyCost;
  const effectiveWorkingDaysPerMonth = WORKING_DAYS_PER_YEAR / 12;
  const developers = totalDays / (effectiveWorkingDaysPerMonth * approximateMonths);

  let roundedDevelopers = Math.round(developers * 5) / 5;
  if (totalDays > 40 && roundedDevelopers < 1) roundedDevelopers = 1;
  return roundedDevelopers;
}

export function formatDeveloperDisplay(developers: number): string {
  if (typeof developers !== "number" || !isFinite(developers)) return "1 développeur";

  const safeDevelopers = Math.max(0, developers);
  const fullTimeDev = Math.floor(safeDevelopers);
  const partialDev = safeDevelopers - fullTimeDev;

  let result = `${safeDevelopers.toLocaleString("fr-FR")} développeur${
    safeDevelopers > 1 ? "s" : ""
  }`;

  if (partialDev > 0) {
    const daysPerWeek = Math.round(partialDev * 5);
    if (fullTimeDev > 0) {
      result += ` (${fullTimeDev} temps plein + 1 à ${daysPerWeek}j/semaine)`;
    } else {
      result += ` (${daysPerWeek}j/semaine)`;
    }
  } else if (fullTimeDev > 0) {
    result += ` (temps plein)`;
  }

  return result;
}

export function calculatePaymentModalityDefaults(
  selectedMissions: Mission[],
  totalCost: number
): PaymentModalityData {
  if (!Array.isArray(selectedMissions)) {
    console.error("calculatePaymentModalityDefaults: selectedMissions must be an array");
    return {
      totalDays: 0,
      defaultDevelopers: 1,
      minDevelopers: 1,
      maxDevelopers: 2,
      defaultMonths: 1,
      defaultMonthlyCost: totalCost || 0,
      minimumMonthlyCost: totalCost || 0,
      maximumMonthlyCost: totalCost || 0,
    };
  }

  const totalDays = selectedMissions.reduce((sum, mission) => {
    if (!mission || typeof mission !== "object") return sum;
    const days = mission.duration_days || parseFloat((mission.duration?.split(" ")[0] || "0").replace(",", "."));
    return sum + (isNaN(days) ? 0 : days);
  }, 0);

  const devRules = calculateDeveloperRules(totalDays);
  const defaultMonthsRaw = calculateProjectMonths(totalDays, devRules.default);
  const defaultMonths = defaultMonthsRaw > 0 ? defaultMonthsRaw : 1;
  const defaultMonthlyCost = calculateMonthlyCostFromTotal(totalCost, defaultMonths);
  const minimumMonthlyCost = calculateMinimumMonthlyCost(devRules.default, totalDays, defaultMonths);

  const maxWith4DevRaw = calculateProjectMonths(totalDays, 4);
  const maxMonths = Math.max(maxWith4DevRaw > 0 ? maxWith4DevRaw : 2, 2);
  const maximumMonthlyCost = calculateMonthlyCostFromTotal(totalCost, maxMonths);

  return {
    totalDays,
    defaultDevelopers: devRules.default,
    minDevelopers: devRules.min,
    maxDevelopers: 4,
    defaultMonths,
    defaultMonthlyCost,
    minimumMonthlyCost,
    maximumMonthlyCost,
  };
}
